import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_STYLE } from '../constants/mapbox';
import { runsPlayedTo, splitTrackAtGaps, toMultiLineString } from '../utils/recordedDayTrack';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

const TRACK_SOURCE = 'day-track';
const PLAYED_SOURCE = 'day-track-played';

/** How long a sidebar width has to hold before the map reframes. */
const REFRAME_DELAY_MS = 250;

/**
 * A whole recorded day on one map: the full track stays visible, the played
 * part is drawn over it, and a marker shows the current position. Map.jsx
 * follows a single position and rebuilds its path every tick, which suits a
 * short dataset; here the camera stays put so the day can be read as a whole.
 *
 * @param {Array<Object>} locations - The day's points, sorted by timestamp
 * @param {number} currentIndex - Position of the current point in `locations`
 * @param {number} rightInset - Width of the sidebar fixed over the map's right edge
 */
const DayTrackMap = ({ locations, currentIndex, rightInset = 0 }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const framedRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);

  const runs = useMemo(() => splitTrackAtGaps(locations), [locations]);

  useEffect(() => {
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAPBOX_STYLE,
      center: [-106.355, 39.6061],
      zoom: 11
    });
    mapRef.current = map;

    const onLoad = () => {
      map.addSource(TRACK_SOURCE, { type: 'geojson', data: toMultiLineString([]) });
      map.addSource(PLAYED_SOURCE, { type: 'geojson', data: toMultiLineString([]) });
      map.addLayer({
        id: TRACK_SOURCE,
        type: 'line',
        source: TRACK_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#9D4EDD', 'line-width': 2, 'line-opacity': 0.45 }
      });
      map.addLayer({
        id: PLAYED_SOURCE,
        type: 'line',
        source: PLAYED_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#ffffff', 'line-width': 3 }
      });
      setMapReady(true);
    };
    map.on('load', onLoad);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  // Frame the day in the part of the map left visible by the timeline and
  // sidebar. The first framing runs without waiting for `load`, which would
  // otherwise finish every tile of the starting view before moving and fetching
  // the real ones. After that, a sidebar width change reframes once it settles.
  useEffect(() => {
    if (locations.length === 0) return void 0;
    const bounds = new mapboxgl.LngLatBounds();
    locations.forEach((location) => bounds.extend([location.lon, location.lat]));
    const frame = (duration) => mapRef.current?.fitBounds(bounds, {
      padding: { top: 80, bottom: 200, left: 60, right: 60 + rightInset },
      maxZoom: 15,
      duration
    });

    if (!framedRef.current) {
      framedRef.current = true;
      frame(0);
      return void 0;
    }
    const timer = setTimeout(() => frame(300), REFRAME_DELAY_MS);
    return () => clearTimeout(timer);
  }, [locations, rightInset]);

  useEffect(() => {
    if (!mapReady) return;
    mapRef.current.getSource(TRACK_SOURCE).setData(toMultiLineString(runs.map((run) => run.coordinates)));
  }, [mapReady, runs]);

  useEffect(() => {
    const location = locations[currentIndex];
    if (!mapReady || !location) return;
    const map = mapRef.current;

    map.getSource(PLAYED_SOURCE).setData(toMultiLineString(runsPlayedTo(runs, currentIndex)));

    if (!markerRef.current) {
      markerRef.current = new mapboxgl.Marker({ color: '#ffffff', scale: 1.1 }).setLngLat([location.lon, location.lat]).addTo(map);
    } else {
      markerRef.current.setLngLat([location.lon, location.lat]);
    }
  }, [mapReady, locations, runs, currentIndex]);

  return <div ref={containerRef} className="mapbox-container" style={{ height: '100%', width: '100%', position: 'relative' }} />;
};

export default DayTrackMap;
